/**
 * Authenticated signup routes that require the user to be logged in.
 * These routes are protected and used in the BFF-driven onboarding flow.
 */
export const AUTHENTICATED_SIGNUP_ROUTES = {
  SIGNUP_TYPE_SELECTION: '/signup/setup/signup-type',
  INVITE_CODE_VALIDATION: '/signup/setup/invite-code',
  INSURANCE_PAYER_SELECTION: '/signup/setup/insurance-payer',
  NAME_DOB_COLLECTION: '/signup/setup/name-dob',
  PROSPECT_NAME_DOB_COLLECTION: '/signup/setup/prospect-name-dob',
  DEMOGRAPHICS_COLLECTION: '/signup/setup/demographics',
  MEMBER_ID_COLLECTION: '/signup/setup/member-id',
} as const

// Terminal screen keys — signup is finished, so there is no screen to route to.
// `home` is what the BFF reports once every required screen is complete;
// `account_creation_success` is hardcoded by the account-creation endpoints. Null is still
// accepted: it is the shape an endpoint that returns no next screen at all sends.
const HOME_SCREEN_KEY = 'home'
const ACCOUNT_CREATION_SUCCESS_SCREEN_KEY = 'account_creation_success'

type TerminalScreenKey = typeof HOME_SCREEN_KEY | typeof ACCOUNT_CREATION_SUCCESS_SCREEN_KEY

/**
 * Whether the BFF is reporting that signup is complete. None of these values has a route of its
 * own — web finishes signup by clearing the session and logging out to /success. Narrowing so a
 * caller that falls through can index SCREEN_KEY_TO_ROUTE_MAPPING without a null check.
 */
export const isSignupComplete = (
  nextScreen: string | null | undefined,
): nextScreen is TerminalScreenKey | null | undefined =>
  nextScreen == null ||
  nextScreen === HOME_SCREEN_KEY ||
  nextScreen === ACCOUNT_CREATION_SUCCESS_SCREEN_KEY

/**
 * Maps BFF onboarding screen keys to frontend routes.
 *
 * This mapping is used by authenticated onboarding screens to navigate
 * based on the `next_screen` value returned by BFF endpoints.
 */
export const SCREEN_KEY_TO_ROUTE_MAPPING: Record<string, string> = {
  signup_type_selection: AUTHENTICATED_SIGNUP_ROUTES.SIGNUP_TYPE_SELECTION,
  invite_code_validation: AUTHENTICATED_SIGNUP_ROUTES.INVITE_CODE_VALIDATION,
  insurance_payer_selection: AUTHENTICATED_SIGNUP_ROUTES.INSURANCE_PAYER_SELECTION,
  name_and_dob_collection: AUTHENTICATED_SIGNUP_ROUTES.NAME_DOB_COLLECTION,
  prospect_name_and_dob_collection: AUTHENTICATED_SIGNUP_ROUTES.PROSPECT_NAME_DOB_COLLECTION,
  demographics_collection: AUTHENTICATED_SIGNUP_ROUTES.DEMOGRAPHICS_COLLECTION,
  member_id_collection: AUTHENTICATED_SIGNUP_ROUTES.MEMBER_ID_COLLECTION,
  create_new_account: AUTHENTICATED_SIGNUP_ROUTES.SIGNUP_TYPE_SELECTION,
}
